/**
 * How a charge reads to a scholar: what it is ("Monthly Rent"), an optional note, and an icon.
 *
 * Older charges were saved with filler text in place of a note: "Regular debit transaction" as the
 * description, and the dropdown label ("Monthly Rent (-$350)") as the reason. Those are hidden here.
 */

const FILLER_NOTES = ['regular debit transaction', 'debit transaction with overdraw fee', 'credit transaction'];

interface ChargeLike {
  description?: string | null;
  reason?: string | null;
  transaction_type?: { name?: string | null } | null;
}

export function chargeTitle(tx: ChargeLike): string {
  return tx.transaction_type?.name?.trim() || tx.description?.trim() || 'Classroom charge';
}

/** A real note from staff, or null when there is only filler. */
export function chargeNote(tx: ChargeLike): string | null {
  const name = (tx.transaction_type?.name || '').trim().toLowerCase();
  const candidates = [tx.description, tx.reason];
  for (const raw of candidates) {
    const text = raw?.trim();
    if (!text) continue;
    const lower = text.toLowerCase();
    if (FILLER_NOTES.includes(lower)) continue;
    // "Monthly Rent" or "Monthly Rent (-$350)": the type name again, not a note.
    if (name && (lower === name || lower.startsWith(`${name} (`))) continue;
    return text;
  }
  return null;
}

const ICONS: Array<[RegExp, string]> = [
  [/\b(rent|mortgage|lease|housing)\b/i, 'solar:home-2-bold-duotone'],
  [/\b(utilit\w*|electric\w*|water|phone|internet|wifi)\b/i, 'solar:bolt-bold-duotone'],
  [/\b(loan|repayment)\b/i, 'solar:hand-money-bold-duotone'],
  [/\b(fine|late|tax|penalty|bathroom|overdraft|overdraw|fee)\b/i, 'solar:alarm-bold-duotone'],
  [/\b(store|purchase|marketplace|headphones?|snack|market)\b/i, 'solar:bag-4-bold-duotone'],
];

export function chargeIcon(tx: ChargeLike): string {
  const title = chargeTitle(tx);
  return ICONS.find(([pattern]) => pattern.test(title))?.[1] ?? 'solar:bill-list-bold-duotone';
}
